/**
 * Name-matching for Kombo `single_select` options.
 *
 * HRIS option labels carry a trailing qualifier the Kula-side name never has —
 * a grade (`Mechanical Engineer (G10)`) or a lifecycle marker
 * (`Sr Director Procurement (inactive)`). Matching the raw strings therefore
 * almost never hits, so both sides are normalised down to the base name first.
 */

interface OptionEntry {
  id?: string;
  label?: string;
  name?: string;
  [key: string]: unknown;
}

/** Kombo uses `label` for inline entries and `name` for referenced ones. */
const getEntryLabel = (entry: OptionEntry): string =>
  entry?.label || entry?.name || '';

/** Only the LAST parenthesised group is a qualifier — `(SCI/SW)` mid-label is part of the name. */
const TRAILING_QUALIFIER = /\s*\([^()]*\)\s*$/;

const INACTIVE_MARKER = /\(\s*inactive\s*\)\s*$/i;

/** Lowercase and collapse runs of whitespace, keeping any qualifier intact. */
const fullForm = (value: string): string =>
  (value || '').replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Lowercase, drop a single trailing parenthesised qualifier, collapse runs of
 * whitespace. Applied to both the incoming name and each option label so they
 * meet on the same footing.
 */
export const normalizeOptionName = (value: string): string =>
  fullForm((value || '').replace(TRAILING_QUALIFIER, ''));

/**
 * The forms a string can be compared under, most specific first.
 *
 * Both sides need both forms because stripping exactly one qualifier per side
 * leaves them off by one whenever the incoming name already ends in a
 * meaningful group: `Computational Biologist (SCI/SW)` against
 * `Computational Biologist (SCI/SW) (G6)` only meets when the name's FULL form
 * is compared to the label's BASE form.
 */
const comparableForms = (value: string): string[] => {
  const full = fullForm(value);
  const base = normalizeOptionName(value);
  return full === base ? [full] : [full, base];
};

/** An option Workday keeps for history but should never be hired against. */
export const isInactiveOption = (entry: OptionEntry): boolean =>
  INACTIVE_MARKER.test(getEntryLabel(entry));

/**
 * Finds the one active option matching `name`, and returns its entry.
 *
 * Matching runs in two tiers so precision wins over reach: an exact
 * label match is taken first, and only if that finds nothing do base names
 * (grade qualifier removed) get compared. Without the tiers, the name
 * `Recruiter (G8)` would tie against `Recruiter (G9)` and `(G10)` on the shared
 * base `recruiter` and be discarded as ambiguous, despite naming one exactly.
 *
 * Returns `undefined` when nothing matches OR when several options match at the
 * winning tier — stripping grades collapses `Recruiter (G8)`, `(G9)` and
 * `(G10)` onto one base name, and guessing among them would silently write the
 * wrong profile into payroll. A blank field costs the user one click; a wrong
 * prefill can be submitted unnoticed.
 */
export const findOptionByName = (
  entries: OptionEntry[] | undefined,
  name: string | undefined | null
): OptionEntry | undefined => {
  const targets = comparableForms(name || '');
  if (!targets[0]) return undefined;

  const candidates = (entries || []).filter(
    (entry) => entry?.id && !isInactiveOption(entry)
  );

  const exact = candidates.filter(
    (entry) => fullForm(getEntryLabel(entry)) === targets[0]
  );
  if (exact.length === 1) return exact[0];

  const loose = candidates.filter((entry) =>
    comparableForms(getEntryLabel(entry)).some((form) => targets.includes(form))
  );
  return loose.length === 1 ? loose[0] : undefined;
};
